<template lang="html">
  <div class="cw-request-account-deletion is-readable-width auto-margin">
    <section class="section content">
      <h1>Request Account Deletion</h1>
      <hr />
      <p>
        <strong>Rolloff Electronic Dispatch</strong> allows you to request the
        deletion of your account and associated personal data. Please review the
        information below and submit a request using the form at the bottom of
        this page.
      </p>

      <h3>How to Request Account Deletion</h3>
      <ol>
        <li>
          Enter the email address associated with your Rolloff Electronic
          Dispatch account in the form below.
        </li>
        <li>
          Optionally, provide any additional details to help us locate your
          account.
        </li>
        <li>
          Click <strong>Submit Request</strong>. You will receive a confirmation
          that your request has been received.
        </li>
        <li>
          Our team will verify your identity and process your request within
          <strong>30 days</strong>.
        </li>
      </ol>

      <h3>Data That Will Be Deleted</h3>
      <p>
        Upon processing your request, the following personal data will be
        permanently deleted:
      </p>
      <ul>
        <li>
          Your account credentials (email address, password, and phone number)
        </li>
        <li>Multi-factor authentication enrollment</li>
        <li>User preferences and notification settings</li>
        <li>Activity and session data</li>
      </ul>

      <h3>Data That Will Be Retained</h3>
      <p>
        The following data is associated with your company account rather than
        your individual user account and will be retained for business, legal,
        and accounting purposes:
      </p>
      <ul>
        <li>Order history and purchase orders</li>
        <li>Project and job site records</li>
        <li>Invoices and billing records</li>
      </ul>
      <p>
        This data belongs to the company account and is required for ongoing
        business operations.
      </p>

      <h3>Submit Your Request</h3>
    </section>

    <section class="section is-top-paddingless">
      <form class="form">
        <label class="label font-light">Account Email Address</label>
        <label class="label font-light has-text-grey-medium">
          Enter the email address associated with your Rolloff Electronic
          Dispatch account.
        </label>
        <div class="field">
          <div class="control has-icons-left">
            <span class="icon is-left">
              <i class="las la-2x la-envelope"></i>
            </span>
            <input
              class="input is-underlined is-medium"
              :class="{ 'is-danger': $v.email.$error }"
              type="email"
              placeholder="Email Address"
              v-model="email"
              @input="$v.email.$touch()"
              @focus="$v.email.$touch()"
            />
            <p class="help" v-if="$v.email.$dirty && !$v.email.required">
              This field is required.
            </p>
            <p class="help" v-if="!$v.email.email">
              Not a valid email address.
            </p>
          </div>
        </div>

        <label
          class="label font-light is-padded-large is-bottom-paddingless is-horizontal-paddingless"
          >Additional Details (optional)</label
        >
        <div class="field">
          <div class="control">
            <textarea
              class="textarea is-medium"
              placeholder="Any additional information to help us locate your account"
              rows="4"
              v-model="details"
            >
            </textarea>
          </div>
        </div>
        <div class="field is-grouped is-grouped-right">
          <div class="control">
            <button
              class="button is-medium is-primary is-rounded"
              type="button"
              name="button"
              @click="submit"
            >
              Submit Request
            </button>
          </div>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
let apiHost = process.env.VUE_APP_API_HOST;

import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';

export default {
  name: 'cw-request-account-deletion',
  mixins: [validationMixin],
  props: {},
  data() {
    return {
      email: '',
      details: '',
    };
  },
  validations: {
    email: {
      required,
      email,
    },
  },
  computed: {},
  components: {},
  methods: {
    async submit() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$modal.show('alert', {
          title: 'Email Required',
          message:
            'Please enter the email address associated with your account.',
          type: 'warning',
        });
        return;
      }
      this.$modal.show('wifi-loader', { message: 'Processing...' });

      try {
        await axios.post(`${apiHost}/auth/support`, {
          email: this.email,
          details: `[Account Deletion Request]\n\n${this.details ||
            'No additional details provided.'}`,
          platform: 'Account Deletion Request',
          device: 'N/A',
        });

        this.$modal.hide('wifi-loader');
        this.$modal.show('alert', {
          title: 'Request Received',
          message:
            'Your account deletion request has been received. Our team will process your request within 30 days. You will be contacted at the email address you provided.',
        });
        this.email = '';
        this.details = '';
        this.$v.$reset();
      } catch (err) {
        this.$modal.hide('wifi-loader');
        let res = err.response;
        if (res && res.data) {
          this.$modal.show('alert', {
            title: res.data.title,
            message: res.data.message,
            type: 'warning',
          });
        } else {
          this.$modal.show('alert', {
            title: 'Error',
            message:
              'Unable to submit your request. Please try again or contact support.',
            type: 'warning',
          });
        }
      }
    },
  },
};
</script>

<style lang="stylus" scoped></style>
